// src/utils/format.js
//
// Display formatters lifted verbatim from the legacy monolith so the new
// React components render identical strings. Behavior must match the
// monolith bit-for-bit until the visual-parity gate has cleared.

/**
 * MM/DD/YY or YYYY-MM-DD parser. 2-digit years are assumed to be 20XX
 * (all portfolio dates are post-2000). YYYY-MM-DD is parsed at midnight
 * to avoid timezone drift.
 *
 * @param {string|null|undefined} dateStr
 * @returns {Date | null}
 */
export function parseDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr !== "string") return null;
  if (dateStr.includes("/")) {
    const [m, d, y] = dateStr.split("/");
    const fullYear = parseInt(y, 10) < 100 ? `20${y}` : y;
    return new Date(parseInt(fullYear, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  if (dateStr.includes("T")) return new Date(dateStr.slice(0, 10) + "T00:00:00");
  return new Date(dateStr + "T00:00:00");
}

/** "Mar 12" style; "—" on missing/invalid. Accepts YYYY-MM-DD or full ISO timestamps. */
export function fmtDateShort(dateStr) {
  if (!dateStr) return "—";
  const date = (typeof dateStr === "string" && dateStr.includes("T"))
    ? new Date(dateStr.slice(0, 10) + "T00:00:00")
    : parseDate(dateStr);
  if (!date || isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Mar 12, 2026" style; "—" on missing/invalid. */
export function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const date = parseDate(dateStr);
  if (!date || isNaN(date)) return "—";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Compact sqft. 500 -> "500 sf"; 124547 -> "125k sf"; 1_200_000 -> "1.2M sf".
 * Returns "" (not "—") for null/0/NaN — safe to splat into JSX.
 * Always rounds to the nearest integer before formatting.
 */
export function fmtSqft(sqft) {
  const n = typeof sqft === "number" ? sqft : parseFloat(sqft);
  if (isNaN(n) || n === 0) return "";
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M sf`;
  if (n >= 1_000)     return `${Math.round(n / 1_000)}k sf`;
  return `${Math.round(n)} sf`;
}

/**
 * Full-form sqft — no abbreviation. Always emits an integer with a
 * thousands delimiter and " sf" suffix: e.g. "462,898 sf".
 * Returns "—" for null / undefined / NaN / 0 so it is safe to use
 * directly as a stat value or label (callers need not guard separately).
 */
export function formatSqftFull(sqft) {
  if (sqft == null) return "—";
  const n = typeof sqft === "number" ? sqft : parseFloat(sqft);
  if (!isFinite(n) || n === 0) return "—";
  return `${Math.round(n).toLocaleString("en-US")} sf`;
}

// Current-footprint displays distinguish recorded zero from unavailable data.
// Keep the legacy formatters' missing/zero semantics unchanged elsewhere.
export function fmtFootprintSqft(sqft) {
  return sqft === 0 ? "0 sf" : fmtSqft(sqft);
}

export function formatFootprintSqftFull(sqft) {
  return sqft === 0 ? "0 sf" : formatSqftFull(sqft);
}

/**
 * Integer formatter for KPI tiles. Rounds to nearest integer, formats with
 * en-US thousands separators (comma), returns "—" for null/undefined/NaN/Infinity.
 *
 * @param {number|null|undefined} n
 * @returns {string}
 */
export function fmtInt(n) {
  if (n == null) return "—";
  const rounded = Math.round(n);
  if (!isFinite(rounded)) return "—";
  return rounded.toLocaleString("en-US");
}

// FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: fmtFdob/fmtFdobLong retired — the FDOB
// field is gone. Online Date now uses fmtDateShort (compact ribbons) and
// fmtDate (long-form detail view), both already defined above.

/**
 * ISO 4217 → display symbol map.
 * Covers all codes in CURRENCIES constant (src/app/currencies.js).
 * Codes not listed fall back to the raw code as prefix (see fmtMoney).
 */
const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "CA$",
  SGD: "S$",
  CHF: "CHF",
  JPY: "¥",
  CNY: "¥",
  HKD: "HK$",
  INR: "₹",
  KRW: "₩",
  AED: "AED",
  BRL: "R$",
  ISK: "kr",
};

/**
 * Returns the display symbol for a currency code.
 * Falls back to the raw code (e.g. "XYZ") for unknown currencies.
 *
 * @param {string} code  ISO 4217 currency code
 * @returns {string}
 */
export function getCurrencySymbol(code) {
  return CURRENCY_SYMBOLS[code] ?? code ?? "";
}

/**
 * Money formatter. Accepts number or numeric string. "—" on null/NaN.
 *
 * @param {number|string|null} val
 * @param {string} currency  ISO 4217
 */
export function fmtMoney(val, currency = "USD", { decimals = 0, code = true } = {}) {
  if (val === null || val === undefined) return "—";
  const num = parseFloat(String(val).replace(/,/g, ""));
  if (isNaN(num)) return "—";
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(decimals ? num : Math.round(num));
  const sym = getCurrencySymbol(currency);
  const prefix = sym !== currency ? sym : "";
  return `${prefix || (!code ? currency : "")}${formatted}${code ? ` ${currency}` : ""}`;
}

/**
 * Truncate a body string for inline display in a card / tile / sidebar row.
 * Returns "" for null/undefined/empty input. Trims trailing whitespace
 * before appending the ellipsis so we don't render "lorem ipsum …"
 * (space-then-ellipsis) when the cut lands on a word boundary.
 *
 * P4 Gate 2 — Two consumers across three views: 70-char default for
 * LocationCard (List) + LocationTile (Tiles), and 40 chars for the
 * narrower LocationsMap sidebar row. Caller passes the maxChars; this
 * helper has no knowledge of which view it's serving.
 *
 * @param {string|null|undefined} str
 * @param {number} maxChars  Maximum length INCLUDING the trailing "…" glyph.
 * @returns {string}
 */
export function truncateBody(str, maxChars = 70) {
  if (str == null) return "";
  const s = String(str);
  if (s.length <= maxChars) return s;
  // Reserve one char for the "…" so the visible width never exceeds maxChars.
  return s.slice(0, Math.max(0, maxChars - 1)).trimEnd() + "…";
}

/**
 * Compact money formatter for KPI tiles.
 * ≥ 1 000 000 → "X.XM {CODE}"; ≥ 1 000 → "Xk {CODE}"; otherwise "X {CODE}".
 * Returns "—" on null/undefined/NaN.
 *
 * @param {number|string|null} val
 * @param {string} currency  ISO 4217
 * @returns {string}
 */
export function fmtMoneyCompact(val, currency = "USD") {
  if (val === null || val === undefined) return "—";
  const num = parseFloat(String(val).replace(/,/g, ""));
  if (isNaN(num)) return "—";
  const sym = getCurrencySymbol(currency);
  const prefix = sym !== currency ? sym : "";
  if (num >= 1_000_000) return `${prefix}${+(num / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M ${currency}`;
  if (num >= 1_000)     return `${prefix}${Math.round(num / 1_000)}k ${currency}`;
  return `${prefix}${Math.round(num)} ${currency}`;
}

/**
 * Canonical "not applicable" label — for fields that legitimately do not apply
 * to a given lease or office type (e.g. TI on a no-TI lease, $/sf on a serviced
 * office). Distinct from "—" which means the data exists but hasn't been entered.
 */
export const BLANK_NA = "n/a";

/** Pick the active lease for a location (status === "active", else first). */
export function getActiveLease(location) {
  const leases = location?.leases || [];
  return leases.find((l) => l.status === "active") || leases[0] || null;
}
